import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { MapRef } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import { SPACE_50 } from '@alltrails/denali/tokens';
import { ExploreServiceState, updateDistanceAway } from '../redux/exploreServiceSlice';
import DistanceAway from '../types/DistanceAway';

/**
 * Only to be used by ExploreFilterBar and AllFiltersModal - this hook really is just a way to consolidate duplicate code and logic
 * and let the two mentioned components render a distance away filter for explore in slightly different manners.
 *
 * @param map Mapbox map object
 * @param defaultDistanceAwayMeters default meters to use for the distance away slider when a value hasn't been set
 * @returns an object of values and callbacks to be used by ExploreFilterBar and AllFiltersModal
 */
const useDistanceAwayFilter = (map?: MapRef, defaultDistanceAwayMeters?: number) => {
  const dispatch = useDispatch();
  const { distanceAway, locationResult } = useSelector((state: { exploreService: ExploreServiceState }) => ({
    distanceAway: state.exploreService.distanceAway,
    locationResult: state.exploreService.locationResult
  }));

  // We store a local value for distance away so a user can slide the slider w/o triggering a ton of isochrone requests
  const [localDistanceAway, setLocalDistanceAway] = useState<number | undefined>();

  useEffect(() => {
    setLocalDistanceAway(distanceAway?.distance || defaultDistanceAwayMeters);
  }, [distanceAway?.distance, defaultDistanceAwayMeters]);

  const applyDistanceAway = useCallback(
    (newDistance: number | undefined) => {
      let newDistanceAway: DistanceAway | undefined;
      if (newDistance === undefined) {
        newDistanceAway = undefined;
        setLocalDistanceAway(defaultDistanceAwayMeters);
      } else if (distanceAway) {
        newDistanceAway = { center: distanceAway.center, distance: newDistance };
      } else if (map) {
        newDistanceAway = { center: map.getCenter(), distance: newDistance };
      }
      dispatch(updateDistanceAway(newDistanceAway));
    },
    [defaultDistanceAwayMeters, dispatch, distanceAway, map]
  );

  const distanceAwaySliderSubHeader = useMemo(() => {
    const locationText = locationResult ? locationResult.name : <FormattedMessage defaultMessage="Map location" />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE_50 }}>
        <div>
          <FormattedMessage defaultMessage="Driving distance from:" />
        </div>
        <div>{locationText}</div>
      </div>
    );
  }, [locationResult]);

  const distanceAwaySliderSubHeaderSingleLine = useMemo(() => {
    const location = locationResult ? locationResult.name : <FormattedMessage defaultMessage="map location" />;
    return <FormattedMessage defaultMessage="Driving distance from {location}" values={{ location }} />;
  }, [locationResult]);

  return useMemo(
    () => ({
      applyDistanceAway,
      distanceAway,
      distanceAwaySliderSubHeader,
      distanceAwaySliderSubHeaderSingleLine,
      localDistanceAway,
      setLocalDistanceAway
    }),
    [applyDistanceAway, distanceAway, distanceAwaySliderSubHeader, distanceAwaySliderSubHeaderSingleLine, localDistanceAway]
  );
};

export default useDistanceAwayFilter;
